<template>
  <div class="PmPackingContainerPure" :style="styles">
    <slot
      :packing-container="{
        addElement,
        removeElement,
        updateDimensionsOfElement,
        packedElements,
        contentCanBeHidden,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  ref,
  shallowRef,
  onMounted,
  onBeforeUnmount,
  provide,
  triggerRef,
  watch,
  type Ref,
  type VNode,
} from 'vue'
import deepmerge from 'deepmerge'
import { emitPackingFinished } from '@/events/packing'
import { type PackingContainerId } from '@/constants/persoplan'
import { useCuid } from '@/composition/useCuid'
import {
  packing,
  type PackingItem as PackingItemForPacking,
} from '@/functional/packing'
import {
  useCurrentElement,
  useDebounceFn,
  useMutationObserver,
} from '@vueuse/core'
import {
  packingContainerSettingsKey,
  packingMonitorKey,
} from '@/utilities/inject'
import type { PackingContainerSettings } from '@/components/persoplan/PmCalendar/PmCalendar.vue'
import type { PackingContainerStatus } from '@/composition/usePackingMonitor'
import { packingContainerKey } from './PmPackingContainer'
import { useMounted } from '../../../composition/useMounted'

const DEBUG = false

export type Props = {
  type: PackingContainerId
  triggerPacking?: number
}

const props = withDefaults(defineProps<Props>(), {})

export type PackingContainerSlotProp = {
  addElement: (
    ...args: Parameters<typeof addElement>
  ) => ReturnType<typeof addElement>
  removeElement: (
    ...args: Parameters<typeof removeElement>
  ) => ReturnType<typeof removeElement>
  updateDimensionsOfElement: (
    ...args: Parameters<typeof updateDimensionsOfElement>
  ) => ReturnType<typeof updateDimensionsOfElement>
  packedElements: typeof packedElements.value
  contentCanBeHidden: typeof contentCanBeHidden.value
}

export type Provide = {
  register: (
    ...args: Parameters<typeof registerNestedPackingContainer>
  ) => ReturnType<typeof registerNestedPackingContainer>
  unregister: (
    ...args: Parameters<typeof registerNestedPackingContainer>
  ) => ReturnType<typeof registerNestedPackingContainer>
  start: (
    ...args: Parameters<typeof nestedPackingContainerStart>
  ) => ReturnType<typeof nestedPackingContainerStart>
  finish: (
    ...args: Parameters<typeof nestedPackingContainerFinish>
  ) => ReturnType<typeof nestedPackingContainerFinish>
  /** This status includes all parent containers aswell */
  isPacking: Ref<boolean>
}

export type SlotProps = {
  packingContainer: PackingContainerSlotProp
}

export type Slots = {
  default: (slotProps: SlotProps) => VNode[]
}

const slots = defineSlots<Slots>()

const packedElements = shallowRef<PackingItemForPacking[]>()
const id = useCuid({ slug: true })
const isMounted = useMounted()

const containerId = computed<PackingContainerStatus['id']>(() => {
  return `${props.type}.${id}`
})

const packingContainerSettingsDefaults = {
  observeResize: true,
} as const satisfies PackingContainerSettings

const packingContainerSettings = inject(packingContainerSettingsKey)

const packingContainerSettingsNormalized = computed(() => {
  return deepmerge(
    packingContainerSettingsDefaults,
    packingContainerSettings?.value ?? {}
  )
})

type PackingItem = {
  id: string
  x: number
  width: number
  height: number
  updateDimensions: () => void
  element: HTMLElement
}

const packingItems = shallowRef(new Map<PackingItem['id'], PackingItem>())
const height = ref<number>()
const elRoot = useCurrentElement()
const packDebouncedFn = useDebounceFn(pack, 20)

const styles = computed(() => {
  return {
    height: `${height.value}px`,
  }
})

watch(
  () => props.triggerPacking,
  () => {
    console.log('triggerPacking')
    pack()
  }
)

function packDebounced(reason: string) {
  /**
   * Do nothing if nested packing containers are not packed
   * or the value is undefined which means it’s not clear if
   * nested packing containers exist
   */
  if (allNestedPackingContainersArePacked.value !== true) {
    // console.log(props.type, 'aborted:', reason)
    return
  }

  /**
   * If the content is currently hidden there is no sense
   * in doing packing
   */
  // TODO: Figure out if this is needed or not
  // if (contentCanBeHidden.value === true) {
  //   return
  // }

  // console.log(props.type, 'wants packing:', reason)

  /**
   * Wnen a new packing is requested, we need to set the packing status
   * to undefined again, since the packing is "dirty" or not resolved again
   */
  isPacking.value = undefined

  packDebouncedFn()
}

const resizeObserver = new ResizeObserver(elementsResized)
useMutationObserver(elRoot, onContentsChanged, {
  childList: true,
})

/**
 * Logic for when the packing container is the parent of another packing container
 */

type NestedPackingContainer = {
  id: string
  isPacking: boolean | undefined
}

const nestedPackingContainers = ref(
  new Map<NestedPackingContainer['id'], NestedPackingContainer>()
)

/**
 * undefined = status not clear yet
 * false = packing is not finished yet
 * true = everything is packed
 */
const allNestedPackingContainersArePacked = computed(() => {
  /**
   * We wait for the component to be mounted, because this way we can be sure that
   * nested packing containers are registered already. This is because nested components
   * are mounted inside → outside or child → parent
   */
  if (!isMounted.value) return undefined

  // No nested packing containers exist
  if (nestedPackingContainers.value.size === 0) return true

  for (const container of nestedPackingContainers.value.values()) {
    // Packing of this element has not started
    if (container.isPacking === undefined) return false
    // If a single container is not packed, packing is not done
    if (container.isPacking === true) return false
  }

  return true
})

/**
 * undefined = initial packing has not happenend or last packing is dirty
 * true = is currently packing
 * false = packing is done
 */
const isPacking = ref<boolean | undefined>()

const allParentContainersArePacked = computed(() => {
  // There is no parent, so that’s an early yes
  if (parentPackingContainer === undefined) return true

  return parentPackingContainer.isPacking.value !== false
})

/**
 * Status of all packing containers of the entire tree
 */
const allOfTreeArePacked = computed(() => {
  // Check if this container is currently packing
  if (isPacking.value === true) return false
  if (isPacking.value === undefined) return false

  // Check if nested containers are packed
  if (allNestedPackingContainersArePacked.value !== true) return false

  // Check if parent containers are packed
  if (allParentContainersArePacked.value !== true) return false

  // If code runs until here everything (parent, this, nested) should be packed
  return true
})

const currentAndAllParentContainersArePacked = computed(() => {
  // Current packing container is not finished
  if (isPacking.value !== false) return false

  // Parent packing containers are not finished
  if (allParentContainersArePacked.value !== true) return false

  return true
})

/**
 * Defines if the content can safely be hidden for performance optimization.
 * All of this somehow only works if we derivate the value of this in a watcher.
 * Using a computed property instead does not work
 */
const contentCanBeHidden = ref(false)

watch(
  allOfTreeArePacked,
  () => (contentCanBeHidden.value = allOfTreeArePacked.value === true),
  { immediate: true }
)

/**
 * Trigger the packing when the status of nested packing containers is clear
 */
watch(
  allNestedPackingContainersArePacked,
  () => {
    packDebounced(
      `nestedPackingContainersResolvedOrUpdated (${allNestedPackingContainersArePacked.value})`
    )
  },
  { immediate: true }
)

function registerNestedPackingContainer(options: {
  id: NestedPackingContainer['id']
}) {
  nestedPackingContainers.value.set(options.id, {
    id: options.id,
    isPacking: undefined,
  })
}

function unregisterNestedPackingContainer(options: {
  id: NestedPackingContainer['id']
}) {
  nestedPackingContainers.value.delete(options.id)
}

function nestedPackingContainerUpdatePackingStatus(options: {
  id: NestedPackingContainer['id']
  isPacking: boolean
}) {
  const container = nestedPackingContainers.value.get(options.id)
  if (!container) return

  container.isPacking = options.isPacking
  triggerRef(nestedPackingContainers)
}

function nestedPackingContainerStart(options: {
  id: NestedPackingContainer['id']
}) {
  nestedPackingContainerUpdatePackingStatus({ id: options.id, isPacking: true })
}

function nestedPackingContainerFinish(options: {
  id: NestedPackingContainer['id']
}) {
  nestedPackingContainerUpdatePackingStatus({
    id: options.id,
    isPacking: false,
  })
}

/**
 * Logic for when the packing container is a child of another packing container
 */

const parentPackingContainer = inject(packingContainerKey, undefined)

function maybeRegisterPackingContainer() {
  if (!parentPackingContainer) return
  parentPackingContainer.register({ id })
}

function maybeUnregisterPackingContainer() {
  if (!parentPackingContainer) return
  parentPackingContainer.unregister({ id })
}

function maybeNotifyParentPackingContainerStart() {
  if (!parentPackingContainer) return

  parentPackingContainer.start({ id })
}

function maybeNotifyParentPackingContainerFinish() {
  if (!parentPackingContainer) return
  parentPackingContainer.finish({ id })
}

onMounted(maybeRegisterPackingContainer)
onBeforeUnmount(() => {
  maybeUnregisterPackingContainer()
  // Cleanup resizeObserver
  resizeObserver.disconnect()
})

/**
 * Notify the packing monitor about the packing status
 */
const packingMonitor = inject(packingMonitorKey)

onMounted(() => {
  if (!packingMonitor) return
  if (!containerId.value) return

  packingMonitor.register(containerId.value)

  /**
   * Do at least once packing, for the case that no elements are added the monitoring
   * via the packingMonitor resolves
   */
  packDebounced('mounted')
})

onBeforeUnmount(() => {
  if (!packingMonitor) return
  if (!containerId.value) return

  packingMonitor.unregister(containerId.value)
})

function elementsResized() {
  if (!packingContainerSettingsNormalized.value.observeResize) return

  // TODO: Possible Performance improvement here would be only to update the dimensions
  // for the elements which actually changed
  packingItems.value.forEach((element) => {
    element.updateDimensions()
  })

  packDebounced('elementsResized')
}

function onContentsChanged(mutationList: MutationRecord[]) {
  const childListChanged = mutationList.some((mutation) => {
    return mutation.type === 'childList'
  })

  if (!childListChanged) return
  packDebounced('contentsChanged')
}

function addElement(element: PackingItem) {
  packingItems.value.set(element.id, element)
  resizeObserver.observe(element.element)
  packDebounced('addElement')
}

function removeElement(options: { id: PackingItem['id'] }) {
  const packingItem = packingItems.value.get(options.id)
  if (!packingItem) return

  resizeObserver.unobserve(packingItem.element)
  packingItems.value.delete(packingItem.id)

  packDebounced('removeElement')
}

function updateDimensionsOfElement(options: {
  id: PackingItem['id']
  x: number
  width: number
  height: number
}) {
  const element = packingItems.value.get(options.id)

  if (!element) {
    if (DEBUG && import.meta.env.DEV === true) {
      console.warn(`packingContainer: element with id ${options.id} not found`)
    }

    return
  }

  element.x = options.x
  element.width = options.width
  element.height = options.height

  // console.log(props.type, 'update dimensions', element.width, element.height)

  packDebounced('updateDimensionsOfElement')
}

function pack() {
  if (!elRoot.value) return
  if (!(elRoot.value instanceof HTMLElement)) return
  if (!isMounted.value) return

  // console.log('-- start:', props.type)

  isPacking.value = true
  maybeNotifyParentPackingContainerStart()
  if (packingMonitor) packingMonitor.start(containerId.value)

  const rect = elRoot.value.getBoundingClientRect()
  if (rect.width === 0) {
    if (DEBUG && import.meta.env.DEV === true) {
      console.log('element with width of 0 on', containerId.value)
    }
  }

  const packingItemsForPacking = Array.from(packingItems.value.values()).map(
    (packingItem) => {
      const item: PackingItemForPacking = {
        id: packingItem.id,
        x: packingItem.x,
        y: 0,
        width: packingItem.width,
        height: packingItem.height,
      }

      return item
    }
  )

  const packedData = packing(packingItemsForPacking, {
    containerWidth: rect.width,
    maintainOrder: false,
  })

  if (packedData) {
    packedElements.value = packedData.packedElements
    height.value = packedData.heightOfPackedElements
  }

  isPacking.value = false
  emitPackingFinished(props.type)
  maybeNotifyParentPackingContainerFinish()
  if (packingMonitor) packingMonitor.stop(containerId.value)

  // console.log('-- finish:', props.type, height.value)
}

provide(packingContainerKey, {
  register: registerNestedPackingContainer,
  unregister: unregisterNestedPackingContainer,
  start: nestedPackingContainerStart,
  finish: nestedPackingContainerFinish,
  isPacking: computed(
    () => currentAndAllParentContainersArePacked.value !== true
  ),
})
</script>

<style lang="scss">
.PmPackingContainerPure {
  position: relative;
}
</style>
